import { useEffect, useState, useCallback } from 'react';
import {
  Mail, MessageSquare, Smartphone, Phone, RefreshCw, Send, FileText, Save, RotateCcw,
  CheckCircle2, AlertTriangle, XCircle, MinusCircle, BellRing,
} from 'lucide-react';
import CredentialsManager from './CredentialsManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  success: 'text-green-300 bg-green-500/15 border border-green-500/30',
  warning: 'text-yellow-300 bg-yellow-500/15 border border-yellow-500/30',
  failed: 'text-red-300 bg-red-500/15 border border-red-500/30',
  skipped: 'text-gray-300 bg-white/5 border border-white/10',
};

const STATUS_ICON = {
  success: CheckCircle2,
  warning: AlertTriangle,
  failed: XCircle,
  skipped: MinusCircle,
};

const CHANNEL_ICON = {
  email: Mail,
  whatsapp: MessageSquare,
  sms: Smartphone,
  voice: Phone,
};

const TEMPLATE_LABELS = {
  double_optin: 'Double Opt-in',
  help: 'HELP Reply',
  stop: 'STOP Reply',
  appointment_confirmation: 'Appointment Confirmation',
  appointment_reminder: 'Appointment Reminder',
  appointment_arrival: 'Appointment Arrival',
};


function authHeaders() {
  const token = localStorage.getItem('casa_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function authedFetch(path, init = {}) {
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: { ...authHeaders(), 'Content-Type': 'application/json', ...(init.headers || {}) },
  });
  return res;
}


export function CommunicationsPanel() {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logFilter, setLogFilter] = useState({ channel: '', status: '' });
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTpl, setLoadingTpl] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [voiceTesting, setVoiceTesting] = useState(false);
  const [voiceResult, setVoiceResult] = useState(null);
  const [sentdmTesting, setSentdmTesting] = useState(false);
  const [sentdmResult, setSentdmResult] = useState(null);
  const [notifSettings, setNotifSettings] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await authedFetch('/api/admin/communications/status');
      if (res.ok) setStatus(await res.json());
    } catch (_e) { /* ignore */ }
  }, []);

  const fetchNotifSettings = useCallback(async () => {
    try {
      const res = await authedFetch('/api/admin/notification-settings');
      if (res.ok) setNotifSettings(await res.json());
    } catch (_e) { /* ignore */ }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (logFilter.channel) params.set('channel', logFilter.channel);
      if (logFilter.status) params.set('status', logFilter.status);
      const res = await authedFetch(`/api/admin/notification-logs?${params}`);
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
      }
    } catch (_e) { /* ignore */ }
    setLoadingLogs(false);
  }, [logFilter]);

  const fetchTemplates = useCallback(async () => {
    setLoadingTpl(true);
    try {
      const res = await authedFetch('/api/admin/message-templates');
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
      }
    } catch (_e) { /* ignore */ }
    setLoadingTpl(false);
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchLogs();
    fetchTemplates();
    fetchNotifSettings();
  }, [fetchStatus, fetchLogs, fetchTemplates, fetchNotifSettings]);

  const handleTest = async (e) => {
    e.preventDefault();
    setTesting(true);
    setTestResult(null);
    try {
      const res = await authedFetch('/api/admin/email/test', {
        method: 'POST',
        body: JSON.stringify({ to: testEmail || undefined }),
      });
      const data = await res.json();
      setTestResult(data);
      // refresh logs so the attempt shows up
      fetchLogs();
    } catch (err) {
      setTestResult({ success: false, message: 'Network error reaching the backend.' });
    }
    setTesting(false);
  };

  const handleVoiceTest = async () => {
    setVoiceTesting(true);
    setVoiceResult(null);
    try {
      const res = await authedFetch('/api/admin/voice/test', { method: 'POST' });
      setVoiceResult(await res.json());
      fetchLogs();
    } catch (_e) {
      setVoiceResult({ success: false, message: 'Network error reaching the backend.' });
    }
    setVoiceTesting(false);
  };

  const handleSentdmTest = async () => {
    setSentdmTesting(true);
    setSentdmResult(null);
    try {
      const res = await authedFetch('/api/admin/sentdm/test', { method: 'POST' });
      setSentdmResult(await res.json());
      fetchLogs();
    } catch (_e) {
      setSentdmResult({ success: false, message: 'Network error reaching the backend.' });
    }
    setSentdmTesting(false);
  };

  const updateNotifSettings = async (patch) => {
    const next = { ...(notifSettings || {}), ...patch };
    setNotifSettings(next);
    try {
      const res = await authedFetch('/api/admin/notification-settings', {
        method: 'PUT', body: JSON.stringify(patch),
      });
      if (res.ok) setNotifSettings(await res.json());
    } catch (_e) { /* ignore */ }
  };

  const handleTemplateSave = async (key, body) => {
    const res = await authedFetch(`/api/admin/message-templates/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ body }),
    });
    if (res.ok) fetchTemplates();
  };

  const handleTemplateReset = async (key) => {
    const res = await authedFetch(`/api/admin/message-templates/${key}/reset`, { method: 'POST' });
    if (res.ok) fetchTemplates();
  };

  return (
    <div className="space-y-6" data-testid="comms-panel">
      {/* Encrypted credentials manager — top of the Communications hub
          so admins land on it first. */}
      <CredentialsManager />

      {/* Channel status grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {['email', 'whatsapp', 'sms', 'voice'].map((ch) => {
          const info = status?.[ch] || {};
          const Icon = CHANNEL_ICON[ch];
          return (
            <div
              key={ch}
              className={`rounded-xl p-4 border ${info.enabled ? 'border-green-500/30 bg-green-500/5' : 'border-white/10 bg-[#111022]'}`}
              data-testid={`channel-card-${ch}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${info.enabled ? 'bg-green-500/20 text-green-300' : 'bg-white/5 text-[#A7A3BA]'}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-[#F4F2FF] font-medium capitalize">{ch}</p>
                  <p className="text-xs text-[#A7A3BA]">
                    {info.enabled ? `${info.provider || 'connected'} · enabled` : 'disabled'}
                  </p>
                </div>
              </div>
              <p className="text-xs text-[#A7A3BA]">From: <span className="text-[#F4F2FF]">{info.from || '—'}</span></p>
              <p className="text-xs text-[#A7A3BA]">Admin: <span className="text-[#F4F2FF]">{info.admin_recipient || '—'}</span></p>
            </div>
          );
        })}
      </div>

      {/* Voice alerts toggle + test */}
      <div className="bg-[#111022] border border-white/10 rounded-xl p-5" data-testid="voice-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#FF4DA6]/15 text-[#FFB6D8] flex-shrink-0">
              <BellRing className="w-4 h-4" />
            </div>
            <div>
              <p className="text-[#F4F2FF] font-semibold">Voice-call admin alerts</p>
              <p className="text-xs text-[#A7A3BA] mt-0.5">
                When enabled, the system will place a Twilio voice call to <span className="text-[#F4F2FF]">{status?.voice?.admin_recipient || 'ADMIN_PHONE'}</span> for every new booking and applicant — useful when you can't watch the dashboard.
              </p>
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-[#A7A3BA] cursor-pointer">
            <input
              type="checkbox"
              checked={!!notifSettings?.voice_alerts_enabled}
              onChange={(e) => updateNotifSettings({ voice_alerts_enabled: e.target.checked })}
              className="w-4 h-4 accent-[#FF4DA6]"
              data-testid="voice-alerts-toggle"
            />
            Enabled
          </label>
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={handleVoiceTest}
            disabled={voiceTesting}
            className="px-4 py-2 rounded-lg bg-[#FF4DA6]/15 text-[#FFB6D8] text-sm flex items-center gap-2 hover:bg-[#FF4DA6]/25 disabled:opacity-50"
            data-testid="voice-test-send"
          >
            <Phone className="w-4 h-4" /> {voiceTesting ? 'Calling…' : 'Send a test call'}
          </button>
          {voiceResult && (
            <span
              className={`text-xs px-3 py-1.5 rounded-full ${voiceResult.success ? STATUS_STYLE.success : STATUS_STYLE.failed}`}
              data-testid="voice-test-result"
            >
              {voiceResult.success
                ? `Call placed to ${voiceResult.recipient} (sid ${voiceResult.id || '—'}).`
                : (voiceResult.message || 'Call failed.')}
            </span>
          )}
        </div>
      </div>

      {/* Sent.dm pipeline test */}
      <div className="bg-[#111022] border border-white/10 rounded-xl p-5" data-testid="sentdm-card">
        <p className="text-[#F4F2FF] font-semibold">Sent.dm pipeline</p>
        <p className="text-xs text-[#A7A3BA] mt-0.5 mb-3">
          When SENTDM_API_KEY + SENTDM_TEMPLATE_NAME are set in the backend, all SMS and WhatsApp sends route through Sent.dm v3. Twilio remains the fallback. This button posts an end-to-end test message via your configured Sent.dm template.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={handleSentdmTest}
            disabled={sentdmTesting}
            className="px-4 py-2 rounded-lg bg-[#8B5CFF]/15 text-[#8B5CFF] text-sm flex items-center gap-2 hover:bg-[#8B5CFF]/25 disabled:opacity-50"
            data-testid="sentdm-test-send"
          >
            <Send className="w-4 h-4" /> {sentdmTesting ? 'Sending…' : 'Send Sent.dm test'}
          </button>
          {sentdmResult && (
            <span
              className={`text-xs px-3 py-1.5 rounded-full ${sentdmResult.success ? STATUS_STYLE.success : STATUS_STYLE.failed}`}
              data-testid="sentdm-test-result"
            >
              {sentdmResult.success
                ? `Sent.dm queued msg (${sentdmResult.id || 'id pending'}) via template ${sentdmResult.template}.`
                : (sentdmResult.message || sentdmResult.error || 'Sent.dm send failed.')}
            </span>
          )}
        </div>
      </div>

      {/* Email test */}
      <div className="bg-[#111022] border border-white/10 rounded-xl p-5" data-testid="email-test-card">
        <h3 className="text-[#F4F2FF] font-semibold mb-1">Send a test email</h3>
        <p className="text-xs text-[#A7A3BA] mb-4">
          Verifies the Resend pipeline. Leave blank to send to the configured admin address.
        </p>
        <form onSubmit={handleTest} className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder={status?.email?.admin_recipient || 'admin@yourdomain.com'}
            className="flex-1 px-3 py-2 rounded-lg bg-[#0B0A12] border border-white/10 text-sm text-[#F4F2FF] focus:outline-none focus:border-[#FF4DA6]/50"
            data-testid="email-test-recipient"
          />
          <button
            type="submit"
            disabled={testing}
            className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#FF4DA6] to-[#8B5CFF] text-white text-sm font-medium flex items-center gap-2 disabled:opacity-50"
            data-testid="email-test-send"
          >
            <Send className="w-4 h-4" /> {testing ? 'Sending…' : 'Send test'}
          </button>
        </form>
        {testResult && (
          <div
            className={`mt-3 px-3 py-2 rounded-lg text-sm ${testResult.success ? STATUS_STYLE.success : STATUS_STYLE.failed}`}
            data-testid="email-test-result"
          >
            {testResult.success
              ? `Email queued via Resend (id ${testResult.id || '—'}) to ${testResult.recipient}.`
              : (testResult.message || 'Email send failed.')}
            {testResult.status_code && (
              <span className="ml-2 opacity-75">[HTTP {testResult.status_code}]</span>
            )}
          </div>
        )}
      </div>

      {/* Notification logs */}
      <div className="bg-[#111022] border border-white/10 rounded-xl p-5" data-testid="notif-logs-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-[#F4F2FF] font-semibold">Notification logs</h3>
          <div className="flex items-center gap-2">
            <select
              value={logFilter.channel}
              onChange={(e) => setLogFilter((f) => ({ ...f, channel: e.target.value }))}
              className="bg-[#0B0A12] border border-white/10 rounded-lg px-2 py-1.5 text-xs text-[#F4F2FF]"
              data-testid="logs-filter-channel"
            >
              <option value="">All channels</option>
              <option value="email">Email</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="sms">SMS</option>
              <option value="voice">Voice</option>
            </select>
            <select
              value={logFilter.status}
              onChange={(e) => setLogFilter((f) => ({ ...f, status: e.target.value }))}
              className="bg-[#0B0A12] border border-white/10 rounded-lg px-2 py-1.5 text-xs text-[#F4F2FF]"
              data-testid="logs-filter-status"
            >
              <option value="">All status</option>
              <option value="success">Success</option>
              <option value="warning">Warning</option>
              <option value="failed">Failed</option>
              <option value="skipped">Skipped</option>
            </select>
            <button
              onClick={fetchLogs}
              className="px-2 py-1.5 rounded-lg bg-[#8B5CFF]/15 text-[#8B5CFF] text-xs flex items-center gap-1 hover:bg-[#8B5CFF]/25"
              data-testid="logs-refresh"
            >
              <RefreshCw className={`w-3 h-3 ${loadingLogs ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-[#A7A3BA]">
              <tr>
                <th className="pb-2 pr-3">Status</th>
                <th className="pb-2 pr-3">Channel</th>
                <th className="pb-2 pr-3">Recipient</th>
                <th className="pb-2 pr-3">Subject / context</th>
                <th className="pb-2 pr-3">When</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr><td colSpan="5" className="text-center text-[#A7A3BA] py-6">No notifications yet.</td></tr>
              ) : (
                logs.map((log) => {
                  const Icon = STATUS_ICON[log.status] || MinusCircle;
                  return (
                    <tr key={log.id} className="border-t border-white/5">
                      <td className="py-2 pr-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${STATUS_STYLE[log.status] || STATUS_STYLE.skipped}`}>
                          <Icon className="w-3 h-3" /> {log.status}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-[#F4F2FF] capitalize">{log.channel}</td>
                      <td className="py-2 pr-3 text-[#A7A3BA] break-all">{log.recipient}</td>
                      <td className="py-2 pr-3 text-[#A7A3BA]">
                        {log.subject || log.error || (log.related?.booking_id ? `booking ${log.related.booking_id.slice(0, 8)}` : '—')}
                      </td>
                      <td className="py-2 pr-3 text-[#A7A3BA] whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Templates */}
      <div className="bg-[#111022] border border-white/10 rounded-xl p-5" data-testid="templates-card">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-4 h-4 text-[#FFB6D8]" />
          <h3 className="text-[#F4F2FF] font-semibold">Compliance message templates</h3>
        </div>
        {loadingTpl ? (
          <p className="text-sm text-[#A7A3BA]">Loading templates…</p>
        ) : (
          <div className="space-y-4">
            {templates.map((tpl) => (
              <TemplateRow
                key={tpl.key}
                tpl={tpl}
                onSave={handleTemplateSave}
                onReset={handleTemplateReset}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function TemplateRow({ tpl, onSave, onReset }) {
  const [body, setBody] = useState(tpl.body);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => { setBody(tpl.body); }, [tpl.body]);

  const dirty = body.trim() !== tpl.body.trim();
  const isDefault = body.trim() === tpl.default.trim();

  const save = async () => {
    setSaving(true);
    await onSave(tpl.key, body.trim());
    setSavedAt(Date.now());
    setSaving(false);
  };

  const reset = async () => {
    setSaving(true);
    await onReset(tpl.key);
    setSaving(false);
  };

  return (
    <div className="rounded-lg border border-white/10 bg-[#0B0A12] p-3" data-testid={`template-${tpl.key}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-[#F4F2FF]">{TEMPLATE_LABELS[tpl.key] || tpl.key}</p>
        <span className={`text-xs ${isDefault ? 'text-[#A7A3BA]' : 'text-[#FFB6D8]'}`}>
          {isDefault ? 'Default copy' : 'Customised'}
        </span>
      </div>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
        className="w-full text-sm bg-[#07060B] border border-white/10 rounded-lg px-3 py-2 text-[#F4F2FF] focus:outline-none focus:border-[#FF4DA6]/50"
        data-testid={`template-${tpl.key}-input`}
      />
      <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
        <p className="text-xs text-[#A7A3BA]">{body.length} chars</p>
        <div className="flex items-center gap-2">
          {savedAt && <span className="text-xs text-green-400">Saved.</span>}
          <button
            type="button"
            onClick={reset}
            disabled={saving || isDefault}
            className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-[#A7A3BA] hover:text-[#F4F2FF] flex items-center gap-1 disabled:opacity-40"
            data-testid={`template-${tpl.key}-reset`}
          >
            <RotateCcw className="w-3 h-3" /> Reset
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            className="text-xs px-3 py-1.5 rounded-lg bg-[#27D7FF] text-[#07060B] font-medium flex items-center gap-1 disabled:opacity-40"
            data-testid={`template-${tpl.key}-save`}
          >
            <Save className="w-3 h-3" /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}


export default CommunicationsPanel;
